/**
 * DataPathBadge — perma-visible trust signal in the dashboard topbar.
 *
 *   [BEDROCK · me-central-1]   ← green, when the tenant's AWS is
 *                                connected and bedrock is enabled
 *   [VE GATEWAY · SHARED]      ← violet, default mode (shared VE LLM Gateway)
 *
 * Click → goes to the AWS connection page so the operator can change
 * mode with one click.
 *
 * Reads /api/aws/connection/tenant once per minute. Failures fall back
 * to the VE Gateway mode display silently — this is a marketing/trust
 * surface, never a blocker.
 */
import React, { useEffect, useState } from 'react';
import { Cloud, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function DataPathBadge() {
  const { user } = useAuth();
  const [bedrock, setBedrock] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get('/aws/connection/tenant');
        if (cancelled) return;
        const isBedrock = data?.status === 'connected'
          && Array.isArray(data?.services_enabled)
          && data.services_enabled.includes('bedrock');
        setBedrock(isBedrock ? { region: data.region || 'aws' } : null);
      } catch {
        if (!cancelled) setBedrock(null);
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  if (!user) return null;

  const active = !!bedrock;
  const tone = active
    ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300'
    : 'border-[var(--brand-primary-strong)] bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]';

  return (
    <Link
      to="/dashboard/settings/aws"
      data-testid="data-path-badge"
      title={active ? 'Your AI runs inside your own AWS region.' : 'Using the shared VE LLM Gateway. Connect AWS to keep prompts inside your account.'}
      className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-[0.18em] pal-mono ${tone} hover:opacity-90 transition-opacity`}
    >
      {active ? <Cloud size={11}/> : <Globe size={11}/>}
      {active
        ? <>BEDROCK<span className="opacity-60 mx-1">·</span>{bedrock.region}</>
        : <>VE GATEWAY<span className="opacity-60 mx-1">·</span>SHARED</>}
    </Link>
  );
}
